import { IError } from '@checkout/errors';
import { ICountry, IPhysicalStore, IRegisterFieldsData, RootState } from '@checkout/state';
import { yupToFormErrors } from 'formik';
import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { ObjectSchema } from 'yup';
import { RegisterCustomerClubFields, RegisterFields, RegisterFormWizard } from '..';
import getRegisterCustomerClubFieldsValidationSchema from '../RegisterCustomerClubFields/RegisterCustomerClubFormValidationSchema';
import getRegisterValidationSchema from '../RegisterFields/RegisterFormValidationSchema';
import { ICheckoutContextConfig } from './../../../config/contextCheckoutConfig.d';
import { registerRequest } from './../../../reducers/checkout';
import { getStatesItems } from './../../../utils/address';

interface IOwnProps {
  isFolded: boolean;
  checkoutContextConfig: ICheckoutContextConfig;
  physicalStores?: IPhysicalStore[];
}

interface IConnectedProps {
  errors?: IError;
  countries: ICountry[];
  registerFieldsData: IRegisterFieldsData;
  isMembershipFetching: boolean;
}

interface IConnectetDispatchProps {
  registerRequest: typeof registerRequest;
}

type Props = IOwnProps & IConnectedProps & IConnectetDispatchProps & InjectedIntlProps;

type RegisterSchemaContext = {
  isStateFieldMounted: boolean;
  isPhisicalStoreMounted: boolean;
  isIdentityNumberMounted: boolean;
};

class RegisterForm extends React.Component<Props, {}> {

  registerValidationSchema = () => {
    return getRegisterValidationSchema(this.props.intl);
  }

  registerAdditionalFieldsValidationSchema = () => {
    return getRegisterCustomerClubFieldsValidationSchema(this.props.intl);
  }

  validate = (values: IRegisterFieldsData, validationSchema: ObjectSchema<{}>) => {
    const { countries, physicalStores } = this.props;
    const statesList = getStatesItems(countries, values.register.country);

    const context: RegisterSchemaContext = {
      isStateFieldMounted: statesList !== undefined,
      isPhisicalStoreMounted: physicalStores !== undefined,
      isIdentityNumberMounted: values.register.country === 'SE',
    };

    return validationSchema.validate(values, { abortEarly: false, context })
      .catch((error: any) => {
        throw yupToFormErrors(error);
      });
  }

  validateRegisterFields = (values: IRegisterFieldsData) => {
    const validationSchema = this.registerValidationSchema();
    return this.validate(values, validationSchema);
  }

  validateAdditionalRegisterFields = (values: IRegisterFieldsData) => {
    const validationSchema = this.registerAdditionalFieldsValidationSchema();
    return this.validate(values, validationSchema);
  }

  render() {
    const { checkoutContextConfig, isFolded, registerFieldsData, registerRequest: onSubmit, errors, physicalStores, isMembershipFetching } = this.props;
    const { isEnabled: isCustomerClubFieldsVisible } = checkoutContextConfig.modules.membership.customerClubFields;

    return (
      <RegisterFormWizard
        isCustomerClubFieldsVisible={isCustomerClubFieldsVisible}
        isFolded={isFolded}
        initialValues={registerFieldsData}
        onSubmit={onSubmit}
        errors={errors}
        isFetching={isMembershipFetching}
      >
        <RegisterFormWizard.Step isLast={!isCustomerClubFieldsVisible} validate={this.validateRegisterFields}>
          <RegisterFields />
        </RegisterFormWizard.Step>
        {isCustomerClubFieldsVisible && (
          <RegisterFormWizard.Step isLast={true} validate={this.validateAdditionalRegisterFields}>
            <RegisterCustomerClubFields
              checkoutContextConfig={checkoutContextConfig}
              physicalStores={physicalStores}
            />
          </RegisterFormWizard.Step>
        )}
      </RegisterFormWizard>
    );
  }
}

export default compose<React.ComponentClass<IOwnProps>>(
  injectIntl,
  connect<IConnectedProps, IConnectetDispatchProps>(
    (state: RootState) => ({
      errors: state.checkout.errors,
      countries: state.checkout.countries,
      registerFieldsData: state.checkout.registerFieldsData,
      isMembershipFetching: state.checkout.isMembershipFetching,
    }),
    {
      registerRequest,
    }
  )
)(RegisterForm);
